/**
 * React wrapper for Bryntum FilePicker
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, ButtonConfig, Container, ContainerItemConfig, ContainerLayoutConfig, FileFieldConfig, FilePicker, Layout, MaskConfig, MenuItemConfig, Model, Rectangle, Scroller, ScrollerConfig, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumFilePickerProps =  {
     // Configs only
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    autoUpdateRecord?        : boolean
    bubbleEvents?            : object
    buttonConfig?            : Partial<ButtonConfig>
    callOnFunctions?         : boolean
    centered?                : boolean
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    contentElementCls?       : string|object
    defaultBindProperty?     : string
    defaultFocus?            : (widget: Widget) => boolean
    defaults?                : object
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    fileFieldConfig?         : Partial<FileFieldConfig>
    floating?                : boolean
    hideAnimation?           : boolean|object
    hideWhenEmpty?           : boolean
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    itemCls?                 : string
    lazyItems?               : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?               : object
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    monitorResize?           : boolean
    namedItems?              : Record<string, Partial<ContainerItemConfig>>
    owner?                   : Widget
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    relayStoreEvents?        : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    showAnimation?           : boolean|object
    showTooltipWhenDisabled? : boolean
    tab?                     : boolean|Partial<TabConfig>
    tag?                     : string
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    textContent?             : boolean
    title?                   : string
    ui?                      : string|object
    weight?                  : number

     // Configs and properties
    alignSelf?           : string
    appendTo?            : HTMLElement|string
    cls?                 : string|object
    content?             : string
    dataset?             : object
    disabled?            : boolean
    extraData?           : string|number|boolean|object
    flex?                : number|string
    height?              : number|string
    hidden?              : boolean
    html?                : string|((widget: Widget) => string)
    id?                  : string
    insertBefore?        : HTMLElement|string
    insertFirst?         : HTMLElement|string
    items?               : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?              : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?         : object
    margin?              : number|string
    maxHeight?           : string|number
    maximizeOnMobile?    : number|string
    maxWidth?            : string|number
    minHeight?           : string|number
    minWidth?            : string|number
    readOnly?            : boolean
    record?              : Model
    scrollable?          : Scroller|boolean|Partial<ScrollerConfig>
    strictRecordMapping? : boolean
    tooltip?             : string|Partial<TooltipConfig>
    width?               : number|string
    x?                   : number
    y?                   : number

     // Properties only
    anchorSize?      : number[]
    isSettingValues? : boolean
    isValid?         : boolean
    type?            : string
    values?          : Record<string, object>

     // Events
    onBeforeDestroy?   : ((event: { source: Base }) => void)|string
    onBeforeHide?      : ((event: { source: Widget }) => boolean|void)|string
    onBeforeSetRecord? : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?      : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?        : ((event: {[key: string]: any, type: string}) => void)|string
    onChange?          : ((event: { files: FileList }) => void)|string
    onClear?           : (() => void)|string
    onDestroy?         : ((event: { source: Base }) => void)|string
    onFocusIn?         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?            : ((event: { source: Widget }) => void)|string
    onPaint?           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?       : (() => void)|string
    onResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onShow?            : ((event: { source: Widget }) => void)|string

}

export class BryntumFilePicker extends React.Component<BryntumFilePickerProps> {

    static instanceClass = FilePicker;

    static instanceName = 'FilePicker';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoUpdateRecord',
        'bubbleEvents',
        'buttonConfig',
        'callOnFunctions',
        'centered',
        'config',
        'constrainTo',
        'contentElementCls',
        'defaultBindProperty',
        'defaultFocus',
        'defaults',
        'dock',
        'draggable',
        'fileFieldConfig',
        'floating',
        'hideAnimation',
        'hideWhenEmpty',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'lazyItems',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'namedItems',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAnimation',
        'showTooltipWhenDisabled',
        'tab',
        'tag',
        'textAlign',
        'textContent',
        'title',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'cls',
        'content',
        'dataset',
        'disabled',
        'extraData',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onCatchAll',
        'onChange',
        'onClear',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onShow',
        'readOnly',
        'record',
        'scrollable',
        'strictRecordMapping',
        'tooltip',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'isSettingValues',
        'isValid',
        'type',
        'values'
    ];

    // Component instance
    instance!: FilePicker;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumFilePickerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-file-picker-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
